export interface ProgramTranslation {
  title: string;
  subtitle: string;
  category: string;
  description: string;
  objectives: string[];
  modules: { title: string; topics: string[] }[];
  careers: string[];
  certifications?: string[];
  duration: string;
  credits: string;
  level: string;
}

export interface Program {
  slug: string;
  heroImage: string;
  superviseur: string;
  superviseurLogo: string;
  translations: {
    en: ProgramTranslation;
    fr: ProgramTranslation;
  };
}

export const programs: Program[] = [
  {
    slug: 'informatique',
    heroImage: '/images/research/studio-app.jpg',
    superviseur: 'Université de Douala (UDo) — ENSPD',
    superviseurLogo: '/images/partners/ud.png',
    translations: {
      fr: {
        title: 'Informatique',
        subtitle: 'Développement, Intelligence Artificielle & Cybersécurité',
        category: 'Sciences & Technologies du Numérique',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Informatique d'ESTAD forme des ingénieurs logiciels et des experts du numérique maîtrisant les technologies de l'information les plus récentes. À travers un programme combinant théorie solide et projets pratiques, nos diplômés sont immédiatement opérationnels dans l'industrie et la recherche.",
        objectives: [
          "Maîtriser les langages et paradigmes de programmation modernes (Python, Java, C++, JavaScript)",
          "Concevoir et déployer des architectures logicielles robustes et scalables",
          "Développer des solutions d'intelligence artificielle et de machine learning",
          "Appliquer les meilleures pratiques en cybersécurité et protection des données",
          "Gérer et analyser de grands volumes de données (Big Data, Data Science)"
        ],
        modules: [
          {
            title: "Programmation & Architecture Logicielle",
            topics: ["Développement orienté objet", "Architecture microservices", "DevOps & CI/CD", "API REST & GraphQL"]
          },
          {
            title: "Intelligence Artificielle & Data Science",
            topics: ["Machine Learning & Deep Learning", "Traitement du langage naturel", "Computer Vision", "Big Data Analytics"]
          },
          {
            title: "Cybersécurité & Réseaux",
            topics: ["Sécurité applicative (OWASP)", "Cryptographie", "Ethical Hacking", "Cloud Security"]
          },
          {
            title: "Bases de Données & Systèmes",
            topics: ["SGBD SQL & NoSQL", "Systèmes d'exploitation", "Administration système Linux", "Cloud Computing (AWS, Azure)"]
          }
        ],
        careers: ["Développeur Full-Stack", "Ingénieur DevOps", "Data Scientist", "Architecte SI", "Consultant Cybersécurité", "Chef de Projet IT"],
        certifications: ["BeingCert AI Practitioner (USA)", "AWS Certified Solutions Architect", "Certification OMPI Propriété Intellectuelle"]
      },
      en: {
        title: 'Computer Science',
        subtitle: 'Development, Artificial Intelligence & Cybersecurity',
        category: 'Digital Science & Technologies',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "ESTAD's Computer Science program trains software engineers and digital experts mastering the latest information technologies. Through a curriculum combining solid theory and practical projects, our graduates are immediately operational in industry and research.",
        objectives: [
          "Master modern programming languages and paradigms (Python, Java, C++, JavaScript)",
          "Design and deploy robust and scalable software architectures",
          "Develop artificial intelligence and machine learning solutions",
          "Apply best practices in cybersecurity and data protection",
          "Manage and analyze large volumes of data (Big Data, Data Science)"
        ],
        modules: [
          {
            title: "Programming & Software Architecture",
            topics: ["Object-oriented development", "Microservices architecture", "DevOps & CI/CD", "REST & GraphQL APIs"]
          },
          {
            title: "Artificial Intelligence & Data Science",
            topics: ["Machine Learning & Deep Learning", "Natural Language Processing", "Computer Vision", "Big Data Analytics"]
          },
          {
            title: "Cybersecurity & Networks",
            topics: ["Application security (OWASP)", "Cryptography", "Ethical Hacking", "Cloud Security"]
          },
          {
            title: "Databases & Systems",
            topics: ["SQL & NoSQL DBMS", "Operating Systems", "Linux system administration", "Cloud Computing (AWS, Azure)"]
          }
        ],
        careers: ["Full-Stack Developer", "DevOps Engineer", "Data Scientist", "IS Architect", "Cybersecurity Consultant", "IT Project Manager"],
        certifications: ["BeingCert AI Practitioner (USA)", "AWS Certified Solutions Architect", "WIPO Intellectual Property Certification"]
      }
    }
  },
  {
    slug: 'electricite-energies',
    heroImage: '/images/campus/espace-01.jpg',
    superviseur: 'Université de Douala (UDo) — ENSPD',
    superviseurLogo: '/images/partners/ud.png',
    translations: {
      fr: {
        title: 'Électricité & Énergies Renouvelables',
        subtitle: 'Smart Grids, Solaire, Éolien & Transition Énergétique',
        category: 'Génie Électrique & Énergie Durable',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Électricité & Énergies Renouvelables répond à l'un des défis majeurs de l'Afrique centrale : la transition vers des sources d'énergie durables et l'électrification des zones rurales. Nos ingénieurs maîtrisent l'ensemble des technologies de production, transport et distribution de l'énergie.",
        objectives: [
          "Concevoir et dimensionner des installations photovoltaïques et éoliennes",
          "Analyser et optimiser les réseaux électriques intelligents (Smart Grids)",
          "Intégrer les sources d'énergie renouvelable dans les réseaux existants",
          "Assurer la maintenance des équipements électriques industriels",
          "Réaliser des audits énergétiques et proposer des solutions d'efficacité"
        ],
        modules: [
          {
            title: "Énergie Solaire & Photovoltaïque",
            topics: ["Technologie des panneaux solaires", "Dimensionnement de systèmes PV", "Stockage par batteries", "Systèmes off-grid et on-grid"]
          },
          {
            title: "Réseaux Électriques & Smart Grids",
            topics: ["Architecture des réseaux de distribution", "Automatisation des réseaux", "Compteurs intelligents (AMI)", "Gestion de l'énergie (EMS)"]
          },
          {
            title: "Électronique de Puissance",
            topics: ["Convertisseurs AC/DC et DC/DC", "Onduleurs photovoltaïques", "Variateurs de vitesse", "Protection des réseaux"]
          },
          {
            title: "Audit Énergétique & Réglementation",
            topics: ["Efficacité énergétique des bâtiments", "Réglementation thermique", "Bilan carbone", "Normes IEC & CEI"]
          }
        ],
        careers: ["Ingénieur Énergie Solaire", "Chef de Projet Smart Grid", "Auditeur Énergétique", "Technicien en Maintenance Électrique", "Conseiller en Transition Énergétique"],
        certifications: ["Certification IRENA (Agence Internationale des Énergies Renouvelables)", "Habilitations électriques B2V/BR/BC"]
      },
      en: {
        title: 'Electricity & Renewable Energies',
        subtitle: 'Smart Grids, Solar, Wind & Energy Transition',
        category: 'Electrical Engineering & Sustainable Energy',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "The Electricity & Renewable Energies program addresses one of Central Africa's major challenges: the transition to sustainable energy sources and rural electrification. Our engineers master all energy production, transmission, and distribution technologies.",
        objectives: [
          "Design and size photovoltaic and wind installations",
          "Analyze and optimize intelligent electrical networks (Smart Grids)",
          "Integrate renewable energy sources into existing grids",
          "Ensure maintenance of industrial electrical equipment",
          "Perform energy audits and propose efficiency solutions"
        ],
        modules: [
          {
            title: "Solar & Photovoltaic Energy",
            topics: ["Solar panel technology", "PV system sizing", "Battery storage", "Off-grid and on-grid systems"]
          },
          {
            title: "Electrical Networks & Smart Grids",
            topics: ["Distribution network architecture", "Grid automation", "Smart meters (AMI)", "Energy management systems (EMS)"]
          },
          {
            title: "Power Electronics",
            topics: ["AC/DC and DC/DC converters", "PV inverters", "Variable speed drives", "Network protection"]
          },
          {
            title: "Energy Audit & Regulation",
            topics: ["Building energy efficiency", "Thermal regulation", "Carbon footprint", "IEC & CEI standards"]
          }
        ],
        careers: ["Solar Energy Engineer", "Smart Grid Project Manager", "Energy Auditor", "Electrical Maintenance Technician", "Energy Transition Consultant"],
        certifications: ["IRENA Certification (International Renewable Energy Agency)", "B2V/BR/BC electrical authorizations"]
      }
    }
  },
  {
    slug: 'reseaux-telecoms',
    heroImage: '/images/research/cati-lab.jpg',
    superviseur: 'Université de Douala (UDo) — ENSPD',
    superviseurLogo: '/images/partners/ud.png',
    translations: {
      fr: {
        title: 'Génie Réseaux & Télécommunications',
        subtitle: 'Réseaux Mobiles 5G, Fibre Optique & Infrastructures Critiques',
        category: 'Télécommunications & Réseaux',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Génie Réseaux & Télécommunications forme des ingénieurs spécialisés dans la conception, le déploiement et la maintenance des infrastructures de communication modernes. Au sein du Centre CATI-OMPI d'ESTAD, les étudiants travaillent sur des projets réels liés aux réseaux 5G, à la fibre optique et aux systèmes de télécommunication satellite.",
        objectives: [
          "Concevoir et dimensionner des réseaux de télécommunications fixes et mobiles",
          "Déployer des infrastructures de câblage en fibre optique",
          "Configurer et administrer des équipements réseaux (Cisco, Juniper, Huawei)",
          "Analyser les performances et optimiser les réseaux (QoS, QoE)",
          "Sécuriser les infrastructures télécom contre les cybermenaces"
        ],
        modules: [
          {
            title: "Réseaux Mobiles 4G/5G",
            topics: ["Architecture LTE et 5G NR", "Planification des réseaux radio", "Protocoles RAN/Core Network", "Small Cells & Densification"]
          },
          {
            title: "Fibre Optique & Transmission",
            topics: ["Technologies FTTH/FTTB", "DWDM et multiplexage optique", "Mesures et tests OTDR", "Ingénierie de câblage"]
          },
          {
            title: "Protocoles & Architecture IP",
            topics: ["TCP/IP, BGP, OSPF, MPLS", "SDN & NFV", "IPv6 & transition IP", "VoIP & qualité de service"]
          },
          {
            title: "Systèmes Embarqués & IoT",
            topics: ["Protocoles IoT (MQTT, LoRaWAN)", "Systèmes temps réel (RTOS)", "Microcontrôleurs et FPGAs", "Edge Computing"]
          }
        ],
        careers: ["Ingénieur Réseaux Télécoms", "Expert 5G & Mobile", "Technicien Fibre Optique", "Architecte Réseau", "Ingénieur IoT", "Consultant Télécom"],
        certifications: ["CCNA/CCNP (Cisco)", "Huawei HCIA Datacom", "BeingCert Network Professional"]
      },
      en: {
        title: 'Network & Telecommunications Engineering',
        subtitle: '5G Mobile Networks, Fiber Optics & Critical Infrastructure',
        category: 'Telecommunications & Networks',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "The Network & Telecommunications Engineering program trains engineers specialized in the design, deployment, and maintenance of modern communication infrastructures. Within ESTAD's WIPO-CATI Center, students work on real projects related to 5G networks, fiber optics, and satellite telecommunication systems.",
        objectives: [
          "Design and size fixed and mobile telecommunication networks",
          "Deploy fiber optic cabling infrastructures",
          "Configure and administer network equipment (Cisco, Juniper, Huawei)",
          "Analyze performance and optimize networks (QoS, QoE)",
          "Secure telecom infrastructures against cyber threats"
        ],
        modules: [
          {
            title: "4G/5G Mobile Networks",
            topics: ["LTE and 5G NR architecture", "Radio network planning", "RAN/Core Network protocols", "Small Cells & Densification"]
          },
          {
            title: "Fiber Optics & Transmission",
            topics: ["FTTH/FTTB technologies", "DWDM and optical multiplexing", "OTDR measurements and tests", "Cabling engineering"]
          },
          {
            title: "IP Protocols & Architecture",
            topics: ["TCP/IP, BGP, OSPF, MPLS", "SDN & NFV", "IPv6 & IP transition", "VoIP & quality of service"]
          },
          {
            title: "Embedded Systems & IoT",
            topics: ["IoT protocols (MQTT, LoRaWAN)", "Real-time systems (RTOS)", "Microcontrollers and FPGAs", "Edge Computing"]
          }
        ],
        careers: ["Telecom Network Engineer", "5G & Mobile Expert", "Fiber Optic Technician", "Network Architect", "IoT Engineer", "Telecom Consultant"],
        certifications: ["CCNA/CCNP (Cisco)", "Huawei HCIA Datacom", "BeingCert Network Professional"]
      }
    }
  },
  {
    slug: 'mecatronique',
    heroImage: '/images/research/equipment-07.jpg',
    superviseur: 'Université de Bertoua (UBe) — ESTMRE',
    superviseurLogo: '/images/partners/ube.png',
    translations: {
      fr: {
        title: 'Mécatronique',
        subtitle: 'Robotique, Automatisation Industrielle & Systèmes Intelligents',
        category: 'Génie Mécanique & Automatisation',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La Mécatronique est à la confluence de la mécanique, l'électronique, l'informatique et l'automatique. La filière forme des ingénieurs capables de concevoir et d'intégrer des systèmes industriels intelligents, de la robotique à l'automation des chaînes de production, en passant par les véhicules autonomes.",
        objectives: [
          "Concevoir des systèmes mécaniques en intégrant les capteurs et actionneurs",
          "Programmer des automates (PLC) et contrôleurs de processus industriels",
          "Développer des systèmes robotiques autonomes",
          "Analyser et optimiser les performances des systèmes automatisés",
          "Assurer la maintenance et la mise en service des équipements industriels"
        ],
        modules: [
          {
            title: "Mécanique & Conception Industrielle",
            topics: ["Conception assistée par ordinateur (CAO/SolidWorks)", "Résistance des matériaux", "Mécanismes et transmissions", "Fabrication et usinage"]
          },
          {
            title: "Automatisation & Robotique",
            topics: ["Automates programmables (Siemens S7, Allen-Bradley)", "Robotique industrielle (KUKA, ABB)", "Visions machine et inspection", "Réseaux industriels (Profibus, EtherNet/IP)"]
          },
          {
            title: "Électronique & Capteurs",
            topics: ["Électronique analogique et numérique", "Capteurs et instrumentation", "Actionneurs hydrauliques & pneumatiques", "Électronique embarquée"]
          },
          {
            title: "Industrie 4.0 & Digital Twin",
            topics: ["Internet industriel des objets (IIoT)", "Jumeaux numériques (Digital Twin)", "Simulation de systèmes", "Maintenance prédictive par IA"]
          }
        ],
        careers: ["Ingénieur Mécatronique", "Technicien en Automatisation", "Ingénieur Robotique", "Chef de Projet Industrie 4.0", "Consultant Maintenance Industrielle"],
        certifications: ["Certification Siemens TIA Portal", "Certification ISO 9001 Qualité Industrielle"]
      },
      en: {
        title: 'Mechatronics',
        subtitle: 'Robotics, Industrial Automation & Intelligent Systems',
        category: 'Mechanical Engineering & Automation',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "Mechatronics is at the crossroads of mechanics, electronics, computer science, and automation. The program trains engineers capable of designing and integrating intelligent industrial systems, from robotics to production line automation and autonomous vehicles.",
        objectives: [
          "Design mechanical systems integrating sensors and actuators",
          "Program automated controllers (PLC) and industrial process controllers",
          "Develop autonomous robotic systems",
          "Analyze and optimize automated system performance",
          "Ensure maintenance and commissioning of industrial equipment"
        ],
        modules: [
          {
            title: "Mechanics & Industrial Design",
            topics: ["Computer-Aided Design (CAD/SolidWorks)", "Strength of materials", "Mechanisms and transmissions", "Manufacturing and machining"]
          },
          {
            title: "Automation & Robotics",
            topics: ["Programmable Logic Controllers (Siemens S7, Allen-Bradley)", "Industrial Robotics (KUKA, ABB)", "Machine vision and inspection", "Industrial networks (Profibus, EtherNet/IP)"]
          },
          {
            title: "Electronics & Sensors",
            topics: ["Analog and digital electronics", "Sensors and instrumentation", "Hydraulic & pneumatic actuators", "Embedded electronics"]
          },
          {
            title: "Industry 4.0 & Digital Twin",
            topics: ["Industrial Internet of Things (IIoT)", "Digital Twins", "System simulation", "AI predictive maintenance"]
          }
        ],
        careers: ["Mechatronics Engineer", "Automation Technician", "Robotics Engineer", "Industry 4.0 Project Manager", "Industrial Maintenance Consultant"],
        certifications: ["Siemens TIA Portal Certification", "ISO 9001 Industrial Quality Certification"]
      }
    }
  },
  {
    slug: 'arts-numeriques',
    heroImage: '/images/campus/espace-03.jpg',
    superviseur: 'Université de Douala (UDo)',
    superviseurLogo: '/images/partners/ud.png',
    translations: {
      fr: {
        title: 'Arts Numériques',
        subtitle: 'Design Interactif, Production Audiovisuelle & Marketing Digital',
        category: 'Communication & Arts Numériques',
        duration: 'Bachelor 3 ans',
        credits: '180 crédits',
        level: 'Licence',
        description: "La filière Arts Numériques forme des créatifs et communicants maîtrisant les outils du design contemporain, de la production audiovisuelle et du marketing digital. À l'ère des réseaux sociaux et du contenu immersif, nos diplômés créent l'identité visuelle et la présence numérique des marques et organisations africaines.",
        objectives: [
          "Maîtriser les logiciels de création graphique (Adobe Creative Suite, Figma, Blender)",
          "Produire des contenus audiovisuels de qualité professionnelle",
          "Concevoir des stratégies de marketing digital et de communication",
          "Développer des interfaces utilisateur (UI/UX) ergonomiques et esthétiques",
          "Gérer et animer des communautés sur les réseaux sociaux"
        ],
        modules: [
          {
            title: "Design Graphique & Identité Visuelle",
            topics: ["Typographie et mise en page", "Charte graphique et branding", "Adobe Photoshop, Illustrator & InDesign", "Motion Design (After Effects)"]
          },
          {
            title: "Production Audiovisuelle",
            topics: ["Tournage et éclairage vidéo", "Montage vidéo (Premiere Pro, DaVinci)", "Photographie professionnelle", "Podcast et production audio"]
          },
          {
            title: "UI/UX & Développement Web",
            topics: ["Recherche utilisateur (User Research)", "Wireframing et prototypage (Figma)", "HTML/CSS & JavaScript", "Applications mobiles (React Native, Flutter)"]
          },
          {
            title: "Marketing Digital & Réseaux Sociaux",
            topics: ["SEO/SEA et référencement", "Community management", "Publicité ciblée (Meta, Google Ads)", "Analyse d'audience (Google Analytics)"]
          }
        ],
        careers: ["Designer Graphique", "Directeur Artistique", "Motion Designer", "Community Manager", "Responsable Marketing Digital", "UX/UI Designer"],
        certifications: ["Google Digital Marketing Certificate", "Meta Blueprint", "Adobe Certified Professional"]
      },
      en: {
        title: 'Digital Arts',
        subtitle: 'Interactive Design, Audiovisual Production & Digital Marketing',
        category: 'Communication & Digital Arts',
        duration: '3-year Bachelor',
        credits: '180 credits',
        level: 'Bachelor',
        description: "The Digital Arts program trains creatives and communicators mastering modern design tools, audiovisual production, and digital marketing. In the age of social media and immersive content, our graduates create the visual identity and digital presence of African brands and organizations.",
        objectives: [
          "Master graphic design software (Adobe Creative Suite, Figma, Blender)",
          "Produce professional-quality audiovisual content",
          "Design digital marketing and communication strategies",
          "Develop ergonomic and aesthetic user interfaces (UI/UX)",
          "Manage and moderate social media communities"
        ],
        modules: [
          {
            title: "Graphic Design & Visual Identity",
            topics: ["Typography and layout", "Graphic charter and branding", "Adobe Photoshop, Illustrator & InDesign", "Motion Design (After Effects)"]
          },
          {
            title: "Audiovisual Production",
            topics: ["Video shooting and lighting", "Video editing (Premiere Pro, DaVinci)", "Professional photography", "Podcast and audio production"]
          },
          {
            title: "UI/UX & Web Development",
            topics: ["User Research", "Wireframing and prototyping (Figma)", "HTML/CSS & JavaScript", "Mobile apps (React Native, Flutter)"]
          },
          {
            title: "Digital Marketing & Social Media",
            topics: ["SEO/SEA and search engine optimization", "Community management", "Targeted advertising (Meta, Google Ads)", "Audience analytics (Google Analytics)"]
          }
        ],
        careers: ["Graphic Designer", "Art Director", "Motion Designer", "Community Manager", "Digital Marketing Manager", "UX/UI Designer"],
        certifications: ["Google Digital Marketing Certificate", "Meta Blueprint", "Adobe Certified Professional"]
      }
    }
  },
  {
    slug: 'agroforesterie',
    heroImage: '/images/campus/campus-02.jpg',
    superviseur: "Université de Bertoua (UBe) — ISABEE",
    superviseurLogo: '/images/partners/ube.png',
    translations: {
      fr: {
        title: 'Agroforesterie',
        subtitle: 'Agriculture Durable, Forêt Tropicale & Sécurité Alimentaire',
        category: 'Agriculture & Environnement',
        duration: 'Bachelor 3 ans',
        credits: '180 crédits',
        level: 'Licence',
        description: "L'Agroforesterie forme des professionnels capables d'allier agriculture productive et gestion forestière durable. Dans le contexte du bassin du Congo — l'un des poumons verts de la planète — nos diplômés jouent un rôle clé dans la sécurité alimentaire, la conservation de la biodiversité et le développement rural durable.",
        objectives: [
          "Concevoir et gérer des systèmes agroforestiers adaptés aux conditions tropicales",
          "Évaluer et inventorier les ressources forestières et agricoles",
          "Mettre en place des pratiques agricoles durables respectueuses de l'environnement",
          "Valoriser les produits forestiers non ligneux (PFNL)",
          "Accompagner les communautés rurales dans leurs projets de développement"
        ],
        modules: [
          {
            title: "Systèmes Agroforestiers Tropicaux",
            topics: ["Conception de parcelles agroforestières", "Gestion des cultures associées", "Restauration des paysages dégradés", "Agroécologie"]
          },
          {
            title: "Sciences du Sol & Hydrologie",
            topics: ["Pédologie et fertilité des sols", "Conservation des eaux et sols", "Irrigation et drainage", "Cartographie SIG & télédétection"]
          },
          {
            title: "Gestion des Ressources Naturelles",
            topics: ["Inventaire forestier", "Biodiversité et conservation", "Produits forestiers non ligneux (PFNL)", "Certification FSC"]
          },
          {
            title: "Développement Rural & Économie",
            topics: ["Gestion de projets agricoles", "Chaînes de valeur agricoles", "Financement rural et microfinance", "Politique agricole camerounaise"]
          }
        ],
        careers: ["Ingénieur Agronome", "Forestier-Agroforestier", "Consultant en Développement Rural", "Responsable de Projet Agriculture Durable", "Chargé de Programme ONG Environnement"]
      },
      en: {
        title: 'Agroforestry',
        subtitle: 'Sustainable Agriculture, Tropical Forest & Food Security',
        category: 'Agriculture & Environment',
        duration: '3-year Bachelor',
        credits: '180 credits',
        level: 'Bachelor',
        description: "Agroforestry trains professionals capable of combining productive agriculture with sustainable forest management. In the context of the Congo Basin — one of the planet's green lungs — our graduates play a key role in food security, biodiversity conservation, and sustainable rural development.",
        objectives: [
          "Design and manage agroforestry systems adapted to tropical conditions",
          "Evaluate and inventory forest and agricultural resources",
          "Implement sustainable agricultural practices respectful of the environment",
          "Valorize non-timber forest products (NTFPs)",
          "Support rural communities in their development projects"
        ],
        modules: [
          {
            title: "Tropical Agroforestry Systems",
            topics: ["Agroforestry plot design", "Intercropping management", "Degraded landscape restoration", "Agroecology"]
          },
          {
            title: "Soil Sciences & Hydrology",
            topics: ["Pedology and soil fertility", "Water and soil conservation", "Irrigation and drainage", "GIS mapping & remote sensing"]
          },
          {
            title: "Natural Resource Management",
            topics: ["Forest inventory", "Biodiversity and conservation", "Non-timber forest products (NTFPs)", "FSC Certification"]
          },
          {
            title: "Rural Development & Economy",
            topics: ["Agricultural project management", "Agricultural value chains", "Rural finance and microfinance", "Cameroonian agricultural policy"]
          }
        ],
        careers: ["Agricultural Engineer", "Forester-Agroforestier", "Rural Development Consultant", "Sustainable Agriculture Project Manager", "Environmental NGO Program Officer"]
      }
    }
  },
  {
    slug: 'sciences-bois-foret',
    heroImage: '/images/campus/campus-01.jpg',
    superviseur: "Université de Bertoua (UBe) — ISABEE",
    superviseurLogo: '/images/partners/ube.png',
    translations: {
      fr: {
        title: 'Sciences du Bois & de la Forêt',
        subtitle: "Exploitation Forestière Durable, Transformation du Bois & Gestion des Ressources",
        category: 'Ressources Naturelles & Forêt',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Sciences du Bois & de la Forêt forme des experts en gestion forestière durable et en transformation industrielle du bois. Le Cameroun possède l'une des forêts tropicales les plus riches du monde ; nos diplômés contribuent à sa valorisation responsable tout en préservant cet héritage pour les générations futures.",
        objectives: [
          "Gérer durablement les concessions forestières dans le respect des normes internationales",
          "Maîtriser les procédés de transformation industrielle du bois",
          "Caractériser les propriétés physico-mécaniques des espèces ligneuses",
          "Appliquer les réglementations forestières nationales et internationales (FLEGT)",
          "Valoriser les sous-produits de la transformation du bois"
        ],
        modules: [
          {
            title: "Botanique & Écologie Forestière",
            topics: ["Taxonomie des essences forestières", "Écosystèmes forestiers tropicaux", "Dynamique de la forêt et régénération", "Inventaire forestier et aménagement"]
          },
          {
            title: "Technologie du Bois & Transformation",
            topics: ["Propriétés physiques et mécaniques du bois", "Séchage et traitement du bois", "Techniques de sciage et mesurage", "Fabrication de panneaux dérivés du bois"]
          },
          {
            title: "Exploitation Forestière & Logistique",
            topics: ["Planification de l'exploitation forestière", "Débardage et transport des grumes", "Sécurité et environnement en forêt", "Engins forestiers et maintenance"]
          },
          {
            title: "Commerce du Bois & Réglementation",
            topics: ["Certification FSC et PEFC", "Traçabilité du bois (FLEGT/REDD+)", "Commerce international du bois", "Gestion d'une entreprise forestière"]
          }
        ],
        careers: ["Ingénieur des Eaux et Forêts", "Responsable Exploitation Forestière", "Expert en Transformation du Bois", "Consultant Certification FSC", "Gestionnaire de Concession Forestière"],
        certifications: ["Certification FSC/PEFC", "Brevet de Gestion Forestière (MINFOF)"]
      },
      en: {
        title: 'Wood & Forest Sciences',
        subtitle: "Sustainable Forestry, Wood Processing & Resource Management",
        category: 'Natural Resources & Forestry',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "The Wood & Forest Sciences program trains experts in sustainable forest management and industrial wood processing. Cameroon possesses one of the world's richest tropical forests; our graduates contribute to its responsible valorization while preserving this heritage for future generations.",
        objectives: [
          "Sustainably manage forest concessions in compliance with international standards",
          "Master industrial wood processing procedures",
          "Characterize the physical-mechanical properties of wood species",
          "Apply national and international forest regulations (FLEGT)",
          "Valorize sub-products of wood processing"
        ],
        modules: [
          {
            title: "Botany & Forest Ecology",
            topics: ["Taxonomy of forest species", "Tropical forest ecosystems", "Forest dynamics and regeneration", "Forest inventory and management"]
          },
          {
            title: "Wood Technology & Processing",
            topics: ["Physical and mechanical properties of wood", "Wood drying and treatment", "Sawing and measurement techniques", "Manufacturing of wood-based panels"]
          },
          {
            title: "Forestry & Logistics",
            topics: ["Forestry planning", "Skidding and log transport", "Forest safety and environment", "Forestry machinery and maintenance"]
          },
          {
            title: "Wood Trade & Regulation",
            topics: ["FSC and PEFC Certification", "Wood traceability (FLEGT/REDD+)", "International wood trade", "Forestry company management"]
          }
        ],
        careers: ["Water and Forest Engineer", "Forestry Manager", "Wood Processing Expert", "FSC Certification Consultant", "Forest Concession Manager"],
        certifications: ["FSC/PEFC Certification", "Forest Management Certificate (MINFOF)"]
      }
    }
  },
  {
    slug: 'mines-exploration',
    heroImage: '/images/facilities/facility-01.jpg',
    superviseur: "Université de Bertoua (UBe) — ESTMRE",
    superviseurLogo: '/images/partners/estmre.jpg',
    translations: {
      fr: {
        title: 'Exploration, Production & Transformation Minières',
        subtitle: 'Géologie, Prospection, Extraction & Valorisation des Ressources Minérales',
        category: 'Géologie & Sciences Minières',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Exploration, Production & Transformation Minières prépare des ingénieurs et techniciens hautement qualifiés pour l'industrie extractive camerounaise et africaine. Face à l'immense richesse du sous-sol camerounais (bauxite, cobalt, nickel, uranium, gaz naturel), nos diplômés sont en première ligne pour valoriser ces ressources de façon responsable.",
        objectives: [
          "Réaliser des campagnes de prospection géologique et géophysique",
          "Évaluer les gisements et estimer les ressources et réserves (code JORC)",
          "Concevoir et superviser les opérations d'extraction minière",
          "Appliquer les procédés de traitement et de valorisation des minerais",
          "Assurer la conformité environnementale et sociale (HSE) des opérations minières"
        ],
        modules: [
          {
            title: "Géologie & Prospection",
            topics: ["Géologie structurale et tectonique", "Télédétection et cartographie géologique", "Méthodes géophysiques (sismique, gravimétrie)", "Évaluation des gisements (ressources et réserves)"]
          },
          {
            title: "Techniques d'Extraction",
            topics: ["Mine à ciel ouvert vs souterraine", "Forage et dynamitage", "Engins miniers et maintenance", "Sécurité minière et procédures d'urgence"]
          },
          {
            title: "Traitement des Minerais",
            topics: ["Concassage et broyage", "Séparation gravimétrique et magnétique", "Hydrométallurgie et pyrométallurgie", "Contrôle qualité des produits miniers"]
          },
          {
            title: "Environnement & Réglementation",
            topics: ["Code minier camerounais", "Étude d'impact environnemental (EIE)", "Réhabilitation des sites miniers", "Responsabilité sociale des entreprises (RSE)"]
          }
        ],
        careers: ["Géologue Minier", "Ingénieur d'Exploitation Minière", "Responsable Traitement des Minerais", "Consultant HSE Mines", "Expert en Gestion de Ressources Minérales"],
        certifications: ["Certification Code JORC (Estimations de Ressources)", "Certification HSE Mines (ICMM)"]
      },
      en: {
        title: 'Mining Exploration, Production & Transformation',
        subtitle: 'Geology, Prospecting, Extraction & Valorization of Mineral Resources',
        category: 'Geology & Mining Sciences',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "The Mining Exploration, Production & Transformation program prepares highly qualified engineers and technicians for the Cameroonian and African extractive industry. Facing the immense wealth of Cameroon's subsoil (bauxite, cobalt, nickel, uranium, natural gas), our graduates are on the frontline to valorize these resources responsibly.",
        objectives: [
          "Conduct geological and geophysical prospecting campaigns",
          "Evaluate deposits and estimate resources and reserves (JORC code)",
          "Design and supervise mining extraction operations",
          "Apply mineral treatment and valorization processes",
          "Ensure environmental and social compliance (HSE) of mining operations"
        ],
        modules: [
          {
            title: "Geology & Prospecting",
            topics: ["Structural geology and tectonics", "Remote sensing and geological mapping", "Geophysical methods (seismic, gravity)", "Deposit evaluation (resources and reserves)"]
          },
          {
            title: "Extraction Techniques",
            topics: ["Open-pit vs underground mine", "Drilling and blasting", "Mining machinery and maintenance", "Mining safety and emergency procedures"]
          },
          {
            title: "Mineral Processing",
            topics: ["Crushing and grinding", "Gravimetric and magnetic separation", "Hydrometallurgy and pyrometallurgy", "Quality control of mining products"]
          },
          {
            title: "Environment & Regulation",
            topics: ["Cameroonian mining code", "Environmental Impact Assessment (EIA)", "Mining site rehabilitation", "Corporate Social Responsibility (CSR)"]
          }
        ],
        careers: ["Mining Geologist", "Mining Operation Engineer", "Mineral Processing Manager", "HSE Mining Consultant", "Mineral Resource Management Expert"],
        certifications: ["JORC Code Certification (Resource Estimations)", "HSE Mining Certification (ICMM)"]
      }
    }
  },
  {
    slug: 'mines-industries',
    heroImage: '/images/facilities/admin-building.jpg',
    superviseur: "Université de Bertoua (UBe) — ESTMRE",
    superviseurLogo: '/images/partners/estmre.jpg',
    translations: {
      fr: {
        title: 'Mines & Industries',
        subtitle: "Gestion Industrielle des Ressources, Procédés de Production & Développement Durable",
        category: 'Industrie & Production Minière',
        duration: 'Bachelor 3 ans / Master 2 ans',
        credits: '180 crédits (Licence) — 120 crédits (Master)',
        level: 'Licence & Master',
        description: "La filière Mines & Industries form des gestionnaires et ingénieurs industriels capables de piloter des chaînes de production dans les secteurs extractifs et manufacturiers. Elle prépare à la conduite d'usines minières, à l'optimisation des procédés et à la gestion intégrée des opérations industrielles en Afrique centrale.",
        objectives: [
          "Piloter et optimiser des chaînes de production industrielle",
          "Gérer les approvisionnements et la logistique des opérations minières",
          "Implémenter des systèmes de management de la qualité (ISO 9001, ISO 14001)",
          "Analyser les coûts de production et développer des plans d'affaires industriels",
          "Assurer la sécurité des installations et la conformité réglementaire"
        ],
        modules: [
          {
            title: "Gestion Industrielle & Lean Manufacturing",
            topics: ["Lean Manufacturing & Six Sigma", "Gestion de la production (MRP/ERP)", "Planification et ordonnancement", "Contrôle qualité industriel"]
          },
          {
            title: "Procédés Industriels Miniers",
            topics: ["Préparation mécanique des minerais", "Procédés chimiques de transformation", "Métallurgie extractive", "Contrôle des procédés (Process Control)"]
          },
          {
            title: "Logistique & Chaîne d'Approvisionnement",
            topics: ["Gestion des stocks et entrepôts", "Transport et logistique des matières", "Achats et gestion des fournisseurs", "Optimisation de la chaîne logistique"]
          },
          {
            title: "Management & Finance Industrielle",
            topics: ["Analyse financière et rentabilité", "Gestion de projets industriels (PMP)", "Droit commercial et contrats industriels", "Développement durable et RSE"]
          }
        ],
        careers: ["Directeur d'Usine", "Ingénieur Procédés", "Responsable Qualité Industrielle", "Chef de Projet Industriel", "Consultant en Organisation Industrielle"],
        certifications: ["Certification Six Sigma Green Belt", "PMP (Project Management Professional)", "ISO 9001 Lead Auditor"]
      },
      en: {
        title: 'Mines & Industries',
        subtitle: "Industrial Resource Management, Production Processes & Sustainable Development",
        category: 'Industry & Mining Production',
        duration: '3-year Bachelor / 2-year Master',
        credits: '180 credits (Bachelor) — 120 credits (Master)',
        level: 'Bachelor & Master',
        description: "The Mines & Industries program trains industrial managers and engineers capable of leading production lines in the extractive and manufacturing sectors. It prepares for the management of mining plants, process optimization, and integrated industrial operations management in Central Africa.",
        objectives: [
          "Lead and optimize industrial production lines",
          "Manage supplies and logistics for mining operations",
          "Implement quality management systems (ISO 9001, ISO 14001)",
          "Analyze production costs and develop industrial business plans",
          "Ensure installation safety and regulatory compliance"
        ],
        modules: [
          {
            title: "Industrial Management & Lean Manufacturing",
            topics: ["Lean Manufacturing & Six Sigma", "Production management (MRP/ERP)", "Planning and scheduling", "Industrial quality control"]
          },
          {
            title: "Mining Industrial Processes",
            topics: ["Mechanical ore preparation", "Chemical transformation processes", "Extractive metallurgy", "Process Control"]
          },
          {
            title: "Logistics & Supply Chain",
            topics: ["Warehouse and stock management", "Material transport and logistics", "Purchasing and supplier management", "Supply chain optimization"]
          },
          {
            title: "Industrial Management & Finance",
            topics: ["Financial analysis and profitability", "Industrial project management (PMP)", "Commercial law and industrial contracts", "Sustainable development and CSR"]
          }
        ],
        careers: ["Plant Manager", "Process Engineer", "Industrial Quality Manager", "Industrial Project Manager", "Industrial Organization Consultant"],
        certifications: ["Six Sigma Green Belt Certification", "PMP (Project Management Professional)", "ISO 9001 Lead Auditor"]
      }
    }
  }
];

export function getProgramBySlug(slug: string): Program | undefined {
  return programs.find(p => p.slug === slug);
}
